import { Injectable } from '@angular/core';

import { IFilter } from '../types/filters';

@Injectable({ providedIn: 'root' })
export class OdataService {
  constructor() {}

  getSearchFilters(
    keyword: string,
    searchFields: string[],
    amountFields: string[] = [],
    searchStrategy: 'contains' | 'startsWith' = 'contains'
  ) {
    const filters: string[] = [];
    if (keyword) keyword = keyword.trim();
    if (keyword) {
      const escapedSearchValue = keyword.replace(/'/g, "''");
      let searchFilter = searchFields
        .filter(col => col.length > 0)
        .map(col => `${searchStrategy}(${col},'${escapedSearchValue}')`)
        .join(' or ');

      let amountStr = keyword.replace(',', '');
      // remove dot at the end
      if (amountStr && amountStr.endsWith('.')) {
        amountStr = amountStr.slice(0, -1);
      }

      // check if contains any letter
      var regExp = /[a-z]/i;
      if (
        amountStr &&
        !isNaN(+amountStr) &&
        amountFields.length &&
        !regExp.test(amountStr)
      ) {
        if ((amountStr.includes('.') && amountStr.substring(0, amountStr.indexOf('.')).length <= 16) || amountStr.length <= 16) {
          const amountFilter = amountFields
            .filter(col => col.length > 0)
            .map(col => `${col} eq ${amountStr}`)
            .join(' or ');
          searchFilter += ` or (${amountFilter})`;
        }
      }

      filters.push(searchFilter);
    }

    return filters;
  }

  getSearchFilterString(
    keyword: string,
    searchFields: string[],
    amountFields: string[] = []
  ) {
    const filters = this.getSearchFilters(keyword, searchFields, amountFields);

    if (filters.length) {
      return filters.map(x => `(${x})`).join(' or ');
    }
    return '';
  }
  getFilterStringFromObject(
    filters: { [key: string]: string },
    filterColumns?: IFilter[]
  ) {
    const filterString = Object.keys(filters).map(key => {
      if (!filters[key]) {
        return '';
      }
      if (filterColumns) {
        const column = filterColumns.find(col => col.key === key);
        if (column) {
          if (column.type === 'date') {
            // Ensure the date value is formatted correctly
            const dateValue = new Date(filters[key])
              .toISOString()
              .split('T')[0];
            return `${key} eq ${dateValue}`;
          } else if (column.type === 'date-range') {
            const dateRange = filters[key] as unknown as Date[];
            const startDate = new Date(dateRange[0])
              .toISOString()
              .split('T')[0];
            const endDate = new Date(dateRange[1]).toISOString().split('T')[0];
            return `${key} ge ${startDate} and ${key} le ${endDate}`;
          } else if (column.type === 'number') {
            return `${key} eq ${filters[key]}`;
          } else if (column.type === 'select') {
            if (column.options) {
              const selectedOption = column.options.find(
                option => option.key === filters[key]
              );
              if (selectedOption && selectedOption.onSelect) {
                return selectedOption.onSelect(filters[key]);
              }
              return '';
            } else if (column.url) {
              return `${key} eq '${filters[key]}'`;
            }
            return '';
          } else if (column.type === 'switch') {
            return `${key} eq ${!!filters[key]}`;
          } else {
            return `contains(${key}, '${filters[key]}')`;
          }
        } else {
          return `contains(${key}, '${filters[key]}')`;
        }
      } else {
        return `contains(${key}, '${filters[key]}')`;
      }
    });
    return filterString.filter(i => !!i).join(' and ');
  }

  convertLoadOptionsToOData(loadOptions: any) {
    const params: any = {};

    // Handle $skip
    if (loadOptions.skip !== undefined) {
      params.$skip = loadOptions.skip;
    }

    // Handle $top (equivalent to $take in loadOptions)
    if (loadOptions.take !== undefined) {
      params.$top = loadOptions.take;
    }

    // Handle $orderby
    if (loadOptions.sort) {
      const orderBy = loadOptions.sort
        .map((sort: any) => `${sort.selector} ${sort.desc ? 'desc' : 'asc'}`)
        .join(',');
      params.$orderby = orderBy;
    }

    // Handle $filter
    if (loadOptions.filter) {
      const filter = this.processFilter(loadOptions.filter);
      if (filter) {
        params.$filter = filter;
      }
    }

    return params;
  }

  // Helper function to process filters
  private processFilter(filter: any): string {
    if (Array.isArray(filter[0])) {
      // Complex filter with multiple conditions
      return filter.map(this.processFilter).join(` ${filter[1]} `);
    } else if (filter.length === 3) {
      // Simple filter
      const [field, operator, value] = filter;
      const odataFilter = this.mapOperatorToOData(operator, value, field);

      return odataFilter;
    }
    return '';
  }

  // Helper function to map DevExtreme operators to OData operators
  private mapOperatorToOData(
    operator: string,
    value: any,
    field: string
  ): string {
    const formattedValue =
      typeof value === 'string' ? `'${value.replace(/'/g, "''")}'` : value; // Add quotes for strings
    const operatorMap: { [key: string]: string } = {
      '=': 'eq',
      '<>': 'ne',
      '>': 'gt',
      '>=': 'ge',
      '<': 'lt',
      '<=': 'le',
      contains: 'substringof',
      startswith: 'startswith',
      endswith: 'endswith'
    };
    switch (operator) {
      case '=':
        return `${field} eq ${formattedValue}`;
      case '<>':
        return `${field} ne ${formattedValue}`;
      case '>':
        return `${field} gt ${formattedValue}`;
      case '>=':
        return `${field} ge ${formattedValue}`;
      case '<':
        return `${field} lt ${formattedValue}`;
      case '<=':
        return `${field} le ${formattedValue}`;
      case 'contains':
        return `contains(${field}, ${formattedValue})`;
      case 'startswith':
        return `startswith(${field}, ${formattedValue})`;
      case 'endswith':
        return `endswith(${field}, ${formattedValue})`;
      // case 'in':
      //   return `in(${field}, ${formattedValue})`;
      // case 'not in':
      //   return `not in(${field}, ${formattedValue})`;
      default:
        return operator;
    }
  }
}


